import { NODE_STATUSES, type NodeRole, type NodeStatus, SYSTEM_ROLES, type SystemRole } from "./types";

/**
 * Матрица role/status и терминальность. Один источник правды: просрочка, `skipTerminal`,
 * views и `setStatusMany` считают её этими функциями, а не своими условиями по месту.
 */

const SYSTEM_ROLE_SET = new Set<string>(SYSTEM_ROLES);

export function isSystemRole(role: NodeRole): role is SystemRole {
	return SYSTEM_ROLE_SET.has(role);
}

/**
 * Терминальный статус — «по задаче принято решение».
 * `paused` («взялся и прервался») и `someday` («не брался») терминальными НЕ являются
 * и между собой не сливаются (engineering §8.1).
 */
export function isTerminal(status: NodeStatus | null): boolean {
	return status === "done" || status === "cancelled";
}

/**
 * Какие статусы допустимы для роли.
 * `null` в результате означает «у этой роли статуса нет вообще» (обязан быть NULL).
 */
export function allowedStatuses(role: NodeRole): readonly NodeStatus[] | null {
	switch (role) {
		case "task":
		case "project":
		// Фокус закрывают той же матрицей, что задачу: «отработал» — done, «сорвался» —
		// cancelled, «прервался» — paused. Ради одного лишь done матрицу не сужаем.
		case "focus":
			return NODE_STATUSES;
		case "recurring":
			// Всегда open: выполнение повторяющейся — строки в completions, а не смена статуса.
			return ["open"];
		default:
			// plain и все системные роли — только NULL.
			return null;
	}
}

export function isStatusAllowed(role: NodeRole, status: NodeStatus | null): boolean {
	const allowed = allowedStatuses(role);
	if (allowed === null) {
		return status === null;
	}
	return status !== null && allowed.includes(status);
}

/** Статус по умолчанию при переходе в роль. */
export function defaultStatusFor(role: NodeRole): NodeStatus | null {
	switch (role) {
		case "task":
		case "project":
		case "recurring":
		case "focus":
			return "open";
		default:
			return null;
	}
}

/**
 * Что делать с `completed_at` при смене статуса.
 * Вход в `done` — поставить; ЛЮБОЙ выход из `done`, включая переход в `cancelled`, — очистить.
 */
export function completedAtTransition(prev: NodeStatus | null, next: NodeStatus | null): "set" | "clear" | "keep" {
	if (next === "done") {
		return prev === "done" ? "keep" : "set";
	}
	return prev === "done" ? "clear" : "keep";
}
